"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Bookmark, FileText, Loader2, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocumentCard } from "@/components/document-card";
import { useToast } from "@/hooks/use-toast";
import { LEVEL_LABELS, type JurisdictionLevel } from "@/lib/jurisdiction";
import { saveLookupHandoff } from "@/lib/lookup-handoff";
import { composeLookupSummary } from "@/lib/lookup-summary";
import {
  PlanGateError,
  saveAddress,
  streamRetrieval,
  type LayerTag,
  type RetrievalDocument,
} from "@/lib/retrieval";
import { cn } from "@/lib/utils";

type StreamLayer = LayerTag & { documents: RetrievalDocument[]; done: boolean };

/**
 * The rules lookup — address + topic in, the descending jurisdiction stack
 * filling with live-retrieved legal text as the SSE stream arrives. This is
 * the product's core surface (Phase 5): every document rendered here was
 * fetched from an official source at query time.
 */
export function RulesLookup({
  entitled = false,
  canSave = false,
  onSaved,
  onCompleted,
  initialAddress = "",
  initialTopic = "",
}: {
  /** Subscriber: explanations are requested and rendered. */
  entitled?: boolean;
  /** Show the save-address action (standard/pro plans). */
  canSave?: boolean;
  onSaved?: () => void;
  /** Called when a run ends (success or failure) — the backend records the
      lookup in history at request time, so either way there is a new row. */
  onCompleted?: () => void;
  initialAddress?: string;
  initialTopic?: string;
}) {
  const [address, setAddress] = useState(initialAddress);
  const [topic, setTopic] = useState(initialTopic);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [normalized, setNormalized] = useState<string | null>(null);
  const [layers, setLayers] = useState<StreamLayer[]>([]);
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // The query the current results belong to — read from a ref, not the live
  // inputs, so editing the form after a lookup cannot mislabel the handoff.
  const ranQueryRef = useRef<{ address: string; topic: string } | null>(null);
  const { showToast } = useToast();

  // Hand the finished lookup to the report page (lib/lookup-handoff), so
  // opening its report renders instantly instead of re-retrieving live.
  useEffect(() => {
    const ran = ranQueryRef.current;
    // `error` covers the interrupted-stream case: a partial stack must never
    // be handed to the report as if it were a complete answer.
    if (!finished || error || layers.length === 0 || !ran) return;
    saveLookupHandoff(ran.address, ran.topic, {
      topic: ran.topic,
      normalized_address: normalized ?? undefined,
      layers: layers.map(({ done: _done, ...layer }) => layer),
      errors: [],
    });
  }, [finished, error, layers, normalized]);

  async function run(e: FormEvent) {
    e.preventDefault();
    const addr = address.trim();
    const top = topic.trim();
    if (addr.length < 3) {
      setError("Please enter a full street address.");
      return;
    }
    if (top.length < 2) {
      setError("Please enter a topic — e.g. “opening a restaurant”.");
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    ranQueryRef.current = { address: addr, topic: top };
    setRunning(true);
    setFinished(false);
    setError(null);
    setNormalized(null);
    setLayers([]);

    try {
      await streamRetrieval(addr, top, {
        explain: entitled,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === "resolved") {
            setNormalized(event.normalized_address ?? null);
          } else if (event.type === "layer_start") {
            setLayers((prev) => {
              if (prev.some((l) => l.index === event.layer.index)) return prev;
              const next = [...prev, { ...event.layer, documents: [], done: false }];
              next.sort((a, b) => a.index - b.index);
              return next;
            });
          } else if (event.type === "document") {
            setLayers((prev) =>
              prev.map((l) =>
                l.index === event.layer.index
                  ? { ...l, documents: [...l.documents, event.document] }
                  : l,
              ),
            );
          } else if (event.type === "layer_done") {
            setLayers((prev) =>
              prev.map((l) =>
                l.index === event.layer.index ? { ...l, done: true } : l,
              ),
            );
          } else if (event.type === "error") {
            setError(event.message ?? "The lookup failed.");
          } else if (event.type === "done") {
            setFinished(true);
          }
        },
      });
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        setError(err instanceof Error ? err.message : "Something went wrong.");
        // Settle any layer still marked in-flight: the stream is over, so a
        // spinner would promise progress that cannot arrive.
        setLayers((prev) => prev.map((l) => (l.done ? l : { ...l, done: true })));
      }
    } finally {
      setRunning(false);
      setFinished(true);
      onCompleted?.();
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveAddress(address.trim(), topic.trim());
      showToast("Address saved — monitoring is on.");
      onSaved?.();
    } catch (err) {
      if (err instanceof PlanGateError) {
        showToast(err.message);
      } else {
        showToast(err instanceof Error ? err.message : "Could not save the address.");
      }
    } finally {
      setSaving(false);
    }
  }

  const hasResults = layers.length > 0;
  // The deterministic "in brief" lede (PV-17): composed only from the layer
  // statuses of a COMPLETE stream — a partial stack must never be summarized
  // as if it were the whole answer.
  const summary = finished && !error ? composeLookupSummary(layers) : null;

  return (
    <div>
      <form onSubmit={run} aria-label="Rules lookup">
        <div className="flex flex-col gap-2.5">
          <Input
            inputSize="lg"
            placeholder="123 Main St, Cincinnati, OH 45202"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
            disabled={running}
            autoComplete="street-address"
            aria-label="Street address"
            required
          />
          <div className="flex flex-col gap-2.5 sm:flex-row">
            <Input
              inputSize="lg"
              placeholder="Topic — e.g. opening a restaurant"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={running}
              aria-label="Topic"
              required
            />
            <Button type="submit" size="lg" loading={running} className="sm:w-44">
              {running ? "Retrieving…" : "Get the rules"}
            </Button>
          </div>
        </div>
      </form>

      {error ? (
        <div
          role="alert"
          className="mt-4 rounded-[8px] border border-warn/30 bg-warn/[0.06] px-4 py-3 text-body-sm text-warn"
        >
          {error}
        </div>
      ) : null}

      {hasResults ? (
        <div className="mt-6 animate-rise">
          <div className="flex flex-wrap items-end justify-between gap-3 border-t-2 border-ink pt-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-primary">
                Governing rules
              </p>
              {normalized ? (
                <p className="mt-1 font-serif text-xl font-semibold text-ink">
                  {normalized}
                </p>
              ) : null}
            </div>
            {finished ? (
              <div className="flex items-center gap-2">
                {canSave ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    <Bookmark className="mr-1.5 h-3.5 w-3.5" />
                    {saving ? "Saving…" : "Save & monitor"}
                  </Button>
                ) : null}
                <Button asChild variant="outline" size="sm">
                  <a
                    href={`/report?address=${encodeURIComponent(address.trim())}&topic=${encodeURIComponent(topic.trim())}`}
                  >
                    <FileText className="mr-1.5 h-3.5 w-3.5" />
                    Report
                  </a>
                </Button>
              </div>
            ) : null}
          </div>
          {summary ? (
            <div className="mt-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
                In brief
              </p>
              <p className="mt-1 max-w-prose font-serif text-[15.5px] leading-relaxed text-ink">
                {summary}
              </p>
            </div>
          ) : null}
          <ol className="mt-5">
            {layers.map((layer) => {
              const hasLaw = layer.documents.some((d) => d.status === "ok");
              return (
                <li
                  key={layer.index}
                  className="border-t border-border py-5 first:border-t-0 first:pt-0"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className={cn(
                        "h-2 w-2 shrink-0 rounded-full",
                        hasLaw ? "bg-ink" : "border border-muted bg-transparent",
                      )}
                    />
                    <span className="font-serif text-[13px] font-semibold uppercase tracking-[0.05em] text-ink">
                      {LEVEL_LABELS[layer.level as JurisdictionLevel] ?? layer.level}
                    </span>
                    <span className="text-body-sm text-muted">{layer.name}</span>
                    {!layer.done ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
                    ) : null}
                  </div>
                  <div
                    className={cn(
                      "mt-4 space-y-5 sm:pl-[18px]",
                      layer.documents.length === 0 && layer.done && "hidden",
                    )}
                  >
                    {layer.documents.map((doc, i) => (
                      <DocumentCard key={`${doc.source}-${doc.citation ?? i}`} document={doc} compact />
                    ))}
                  </div>
                  {layer.documents.length === 0 && layer.done ? (
                    <p className="mt-2 font-serif text-body-sm italic text-muted sm:pl-[18px]">
                      No documents for this layer.
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
          {finished && !entitled ? (
            <div className="border-t border-border pt-3 text-body-sm text-muted">
              Plain-language explanations of each rule are part of the paid
              plan.{" "}
              <a href="/pricing" className="text-primary hover:underline">
                See pricing
              </a>
              .
            </div>
          ) : null}
        </div>
      ) : null}

      {hasResults ? (
        <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3 border-t-2 border-ink pt-3.5">
          <p className="max-w-prose text-[12.5px] leading-relaxed text-muted">
            Every document above was retrieved from its official source at
            query time and re-verified against its citation. Prosa provides
            legal information for orientation, not legal advice.
          </p>
          <p className="whitespace-nowrap font-serif text-[13px] italic text-muted">
            Prosa · the record, cited
          </p>
        </div>
      ) : null}
    </div>
  );
}
